import { useNavigate } from '@tanstack/react-router';
import { useEffect, useRef, useState } from 'react';
import { Alert } from '../../components/ui/Alert.tsx';
import { Button } from '../../components/ui/Button.tsx';
import { Spinner } from '../../components/ui/Layout.tsx';
import { formatFull, formatWhen } from '../inbox/format.ts';
import {
  type NotificationItem,
  useMarkNotificationsRead,
  useNotificationsQuery,
} from './use-notifications.ts';

const BELL = 'M6 8a6 6 0 1 1 12 0c0 7 3 8 3 8H3s3-1 3-8M10.3 21a1.9 1.9 0 0 0 3.4 0';

/**
 * The bell (PLAN.md §3.10, M6): opt-outs, review holds, new conversations from unknown numbers, texts
 * that failed or did not go out. Updates live (a Realtime `notif` event invalidates the query).
 */
export function NotificationBell({ workspaceId }: { workspaceId: string }) {
  const [open, setOpen] = useState(false);
  const notifications = useNotificationsQuery(workspaceId);
  const markRead = useMarkNotificationsRead(workspaceId);
  const navigate = useNavigate();
  const button = useRef<HTMLButtonElement>(null);
  const heading = useRef<HTMLHeadingElement>(null);
  const unread = notifications.data?.unread ?? 0;

  function close() {
    setOpen(false);
    button.current?.focus();
  }

  useEffect(() => {
    if (!open) return;
    heading.current?.focus();
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return;
      setOpen(false);
      button.current?.focus();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [open]);

  function openItem(item: NotificationItem) {
    if (!item.readAt) markRead.mutate([item.id]);
    setOpen(false);
    if (item.merchantId) {
      void navigate({ to: '/inbox/$merchantId', params: { merchantId: item.merchantId } });
    }
  }

  return (
    <div className="relative">
      <button
        ref={button}
        type="button"
        onClick={() => setOpen((o) => !o)}
        aria-expanded={open}
        aria-haspopup="dialog"
        aria-label={unread > 0 ? `Notifications (${unread} new)` : 'Notifications'}
        className="relative inline-flex size-10 items-center justify-center rounded-md text-ink-muted hover:bg-surface focus-visible:outline-2 focus-visible:outline-focus-ring"
        data-testid="notification-bell"
      >
        <svg
          viewBox="0 0 24 24"
          className="size-5"
          fill="none"
          stroke="currentColor"
          strokeWidth={1.8}
          strokeLinecap="round"
          strokeLinejoin="round"
          aria-hidden
        >
          <path d={BELL} />
        </svg>
        {unread > 0 ? (
          <span
            className="absolute -top-0.5 -right-0.5 min-w-5 rounded-pill bg-brand px-1 text-center caption leading-5 text-on-brand"
            data-testid="notification-count"
          >
            {unread > 99 ? '99+' : unread}
          </span>
        ) : null}
      </button>

      {open ? (
        <>
          <div className="fixed inset-0 z-30" aria-hidden onClick={close} />
          <div
            role="dialog"
            aria-label="Notifications"
            className="fixed inset-x-2 top-14 z-40 flex max-h-[75vh] flex-col overflow-hidden rounded-lg border border-hairline bg-surface-raised shadow-card md:absolute md:inset-x-auto md:top-11 md:left-0 md:w-96"
            data-testid="notification-panel"
          >
            <div className="flex items-center justify-between gap-2 border-b border-hairline px-3 py-2">
              <h2 ref={heading} tabIndex={-1} className="callout font-semibold text-ink">
                Notifications
              </h2>
              <div className="flex items-center gap-1">
                {unread > 0 ? (
                  <Button
                    variant="ghost"
                    size="sm"
                    busy={markRead.isPending && markRead.variables === null}
                    onClick={() => markRead.mutate(null)}
                  >
                    Mark all read
                  </Button>
                ) : null}
                <Button variant="ghost" size="sm" onClick={close}>
                  Close
                </Button>
              </div>
            </div>
            <div className="overflow-y-auto">
              {notifications.isPending ? (
                <div className="px-3">
                  <Spinner label="Loading notifications" />
                </div>
              ) : notifications.isError ? (
                <div className="p-3">
                  <Alert tone="error">{notifications.error.message}</Alert>
                </div>
              ) : notifications.data.items.length === 0 ? (
                <p className="px-3 py-8 text-center callout text-ink-muted">
                  No notifications yet. Opt-outs, new conversations and texts that did not go out
                  appear here.
                </p>
              ) : (
                <ul className="divide-y divide-hairline">
                  {notifications.data.items.map((item) => (
                    <li key={item.id}>
                      <button
                        type="button"
                        onClick={() => openItem(item)}
                        className={`flex w-full flex-col gap-0.5 px-3 py-2.5 text-left hover:bg-surface focus-visible:outline-2 focus-visible:-outline-offset-2 focus-visible:outline-focus-ring ${
                          item.readAt ? '' : 'bg-brand-tint'
                        }`}
                        data-testid="notification"
                        data-unread={item.readAt ? undefined : 'true'}
                      >
                        <span className="flex items-start justify-between gap-2">
                          <span
                            className={`callout ${item.readAt ? 'text-ink' : 'font-semibold text-ink'}`}
                          >
                            {item.title}
                          </span>
                          <span
                            className="shrink-0 footnote text-ink-muted"
                            title={formatFull(item.createdAt)}
                          >
                            {formatWhen(item.createdAt)}
                          </span>
                        </span>
                        {item.body ? (
                          <span className="break-words footnote text-ink-muted">{item.body}</span>
                        ) : null}
                      </button>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </div>
        </>
      ) : null}
    </div>
  );
}
